import moment from 'moment';
import { KPROXY_VERSION_MAP } from './constants';

// These types are mostly placeholders and are not very accurate...
export interface ConfigMetadata {
  name: string;
  type: string;
  key?: string;
  env?: string;
  arg?: string;
  label?: string;
  description?: string;
  default?: string;

  sudoRequired?: boolean;
  cannotDelete?: boolean;
  resetToDefault?: boolean;
  userControlled?: boolean;
  required?: boolean;
  featureFlag?: string;

  minVersion?: number;
  maxVersion?: number;

  options?: { value: string; label: string; featureFlag?: string }[];
  rules?: string[];

  placeholder?: string;
  autoInsertPrefix?: string;
  dependsOn?: string;
  customSelector?: string;

  specialString?: (interfaceVal: string) => string;
}

export interface LegacyConfigMetadata {
  label: string;
  type: string;
  sudoRequired: boolean;
  userControlled: boolean;
  cannotDelete: boolean;
  resetToDefault: boolean;
  required: boolean;
  rules: ConfigMetadata['rules'];
  placeholder: string;
  autoInsertPrefix: string;
  featureFlag?: string;

  default?: string;
  dependsOn?: string;
  showInputFilter?: boolean;
  customSelector?: string;
  autoShowValue?: string;
  options?: ConfigMetadata['options'];
  argument?: string;
  description?: string;
  minVersion?: ConfigMetadata['minVersion'];
  maxVersion?: ConfigMetadata['maxVersion'];
}

/**
 * Transform data type from json schema to the react component types
 */
export const transformConfigMetadataDataType = (argType: string): string => {
  switch (argType) {
    case 'boolean':
      return 'bool';
    case 'number':
      return 'float';
    case 'integer':
      return 'int';
    default:
      return argType; // Return as is if no mapping is found
  }
};

/**
 * Transforms the new API schema to the old constants format
 */
export const transformConfigMetadataToOldFormat = (
  metadata: { config?: ConfigMetadata[] } | undefined
): Record<string, LegacyConfigMetadata> => {
  if (!metadata || !metadata.config) {
    return {};
  }

  const transformedConfig: Record<string, LegacyConfigMetadata> = {};

  metadata.config.forEach((arg) => {
    const key = arg.name;

    // Determine the 'argument' field: env takes priority over arg
    const argumentValue = arg.env || arg.arg;

    // Determine the type - if options are provided, use 'select'
    const argType = arg.options && arg.options.length > 0 ? 'select' : transformConfigMetadataDataType(arg.type);

    transformedConfig[key] = {
      argument: argumentValue,
      label: arg.label || '',
      type: argType,
      description: arg.description,
      default: arg.default,
      sudoRequired: arg.sudoRequired || false,
      userControlled: arg.userControlled ?? false,
      cannotDelete: arg.cannotDelete || false,
      resetToDefault: arg.resetToDefault || false,
      minVersion: arg.minVersion,
      maxVersion: arg.maxVersion,
      rules: arg.rules || [],
      placeholder: arg.placeholder || '',
      autoInsertPrefix: arg.autoInsertPrefix || '',
      dependsOn: arg.dependsOn,
      required: arg.required || false,
      featureFlag: arg.featureFlag
    };

    // define data type as custom selector
    if (arg.customSelector) {
      transformedConfig[key].type = 'select';
      transformedConfig[key].showInputFilter = true;
      transformedConfig[key].customSelector = arg.customSelector;

      if (arg.customSelector === 'networkInterfaceSelect') {
        arg.specialString = (interfaceVal: string) => (interfaceVal === 'any' ? 'All' : interfaceVal);
      }
    }

    if (arg.dependsOn) {
      transformedConfig[key].autoShowValue = arg.default ?? '';
    }

    // If options are provided, include them in the transformed config
    if (arg.options && arg.options.length > 0) {
      transformedConfig[key].options = arg.options;
    }
  });

  return transformedConfig;
};

export const getRawCapabilityConfigs = (capabilityKey: string): Record<string, unknown> => {
  switch (capabilityKey) {
    default:
      console.error(`No config found for capability key in getRawCapabilityConfigs: ${capabilityKey}`);
      // Return an empty object or throw an error based on your application's needs
      return {};
  }
};

export const capabilitySupportsConfig = (metadata: { config?: unknown[] } | null | undefined): boolean =>
  metadata !== null && metadata !== undefined && Array.isArray(metadata.config) && metadata.config.length > 0;

// A kproxy build is considered "legacy" once it is older than this.
const LEGACY_BUILD_AGE = { value: 1, unit: 'year' } as const;

interface KproxyBuild {
  agent_built_on?: string | null;
  agent_version?: string | null;
}

export function isLegacyKproxyBuild({ agent_built_on, agent_version }: KproxyBuild, now = moment.utc()): boolean {
  const cutoff = now.clone().subtract(LEGACY_BUILD_AGE.value, LEGACY_BUILD_AGE.unit);

  // Primary signal: the build date reported by the agent (e.g. "Mon, 30 Jun 2025").
  let builtOn = agent_built_on ? moment.utc(agent_built_on, 'ddd, D MMM YYYY', true) : null;

  // Fallback: the known release date for this build when agent_built_on is missing/unparseable.
  if (!builtOn || !builtOn.isValid()) {
    const entry = agent_version ? KPROXY_VERSION_MAP[agent_version] : undefined;
    builtOn = entry ? moment.utc(entry.builtOn) : null;
  }

  return !!builtOn && builtOn.isValid() && builtOn.isBefore(cutoff);
}
